import { useState } from 'react';
import { MessagesSquare, Pencil, Trash2 } from 'lucide-react';

import { useAgentPanelContext } from '@/app/agentConversation/_context';
import { DATE_FORMATS, formatDate } from '@/helpers/date';
import { cn } from '@/helpers/shadcn';
import { mutateListOnDelete, mutateListOnPut } from '@/helpers/swr';

import { Button } from '@/app/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/app/ui/empty';
import { Error } from '@/app/ui/error';
import { Loading } from '@/app/ui/loading';
import DeleteAgentConversationDialog from '@/app/agentConversation/DeleteAgentConversationDialog';
import EditAgentConversationDialog from '@/app/agentConversation/EditAgentConversationDialog';
import { useAgentConversationObject, useAgentConversations } from '@/data/agentConversation';

/** The user's conversations, most recent first: open one, edit it or delete it. */
const History = ({ onClose }) => {
  const conversations = useAgentConversations();
  if (conversations.isLoading && !conversations.data) return <Loading />;
  if (conversations.error) {
    return (
      <div className="p-4">
        <Error message={conversations.error.message} />
      </div>
    );
  }
  return <HistoryList onClose={onClose} />;
};

const HistoryList = ({ onClose }) => {
  const panel = useAgentPanelContext();
  const conversations = useAgentConversations();
  const [editUid, setEditUid] = useState(null);
  const [deleteUid, setDeleteUid] = useState(null);

  const handleSelect = (uid) => {
    panel.selectConversation(uid);
    onClose();
  };

  const handleCloseEdit = () => setEditUid(null);
  const handleCloseDelete = () => setDeleteUid(null);

  const handleEdited = (updated) => conversations.mutate((current) => mutateListOnPut(current, updated), { revalidate: false });

  // The dialog is unmounted before its conversation leaves the cache: it reads
  // the title from that entry, and would render once more without it.
  const handleDeleted = (uid) => {
    setDeleteUid(null);
    if (uid === panel.conversationUid) panel.startNewConversation();
    return conversations.mutate((current) => mutateListOnDelete(current, uid), { revalidate: false });
  };

  if (!conversations.data.query.length) {
    return (
      <Empty className="border-0" data-testid="agent-history-empty">
        <EmptyHeader>
          <EmptyMedia>
            <MessagesSquare />
          </EmptyMedia>
          <EmptyTitle>No Conversations Yet</EmptyTitle>
          <EmptyDescription>Conversations you have with the assistant appear here.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-2" data-testid="agent-history">
      {conversations.data.query.map((uid) => (
        <HistoryItem key={uid} uid={uid} isCurrent={uid === panel.conversationUid} onSelect={handleSelect} onEdit={setEditUid} onDelete={setDeleteUid} />
      ))}
      {editUid && <EditAgentConversationDialog uid={editUid} onClose={handleCloseEdit} onSuccess={handleEdited} />}
      {deleteUid && <DeleteAgentConversationDialog uid={deleteUid} onClose={handleCloseDelete} onSuccess={handleDeleted} />}
    </div>
  );
};

const HistoryItem = ({ uid, isCurrent, onSelect, onEdit, onDelete }) => {
  const conversation = useAgentConversationObject(uid);
  const handleSelect = () => onSelect(uid);
  const handleEdit = () => onEdit(uid);
  const handleDelete = () => onDelete(uid);

  return (
    <div className={cn('group/item hover:bg-accent flex items-center gap-1 rounded-md pr-1', isCurrent && 'bg-accent')} data-testid={`agent-conversation-${uid}`}>
      <Button variant="ghost" className="h-auto min-w-0 flex-1 flex-col items-start gap-0 px-2.5 py-2 hover:bg-transparent" onClick={handleSelect}>
        <span className="w-full truncate text-left text-sm font-normal">{conversation.title}</span>
        <span className="text-muted-foreground text-xs font-normal">{formatDate(conversation.update_ts ?? conversation.create_ts, DATE_FORMATS.DATE_TIME)}</span>
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="opacity-0 group-hover/item:opacity-100 focus-visible:opacity-100"
        onClick={handleEdit}
        aria-label="Edit"
        data-testid="agent-conversation-edit"
      >
        <Pencil />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="opacity-0 group-hover/item:opacity-100 focus-visible:opacity-100"
        onClick={handleDelete}
        aria-label="Delete"
        data-testid="agent-conversation-delete"
      >
        <Trash2 />
      </Button>
    </div>
  );
};

export default History;
